/**
 * 제출 직전 날짜 경고 — "이 수강권으로 이 날짜에 잡아도 되나"를 **보낼 요청 내용**으로 판정한다.
 *
 * 원인: 9/10 수업을 1/15부터 시작하는 수강권으로 잡는 일이 잦았다. 서버는 개인 수강권의
 * 시작일·만료일을 검사하지 않아(공유만 검사) 그대로 저장된다. 그래서 막지는 않고, 확인 창으로
 * 한 번 더 묻는다(설계 `rappo/docs/MEMBERSHIP_DEFAULT_SELECTION_PLAN.md` 4절).
 *
 * ⚠️ 입력은 화면 상태가 아니라 **실제로 서버에 보낼 값**이다. 회원별 수강권 번호·수업 시각 목록·
 * 공유 풀 번호를 그대로 받고, 수강권 객체는 그 번호로 지금 받은 목록에서 찾는다. 화면 순번으로
 * 다시 고르면 "확인한 수강권"과 "보낸 수강권"이 갈릴 수 있다.
 *
 * ⚠️ 이것은 **경고**이지 서버와 같은 허용 판정이 아니다. 예약 가능 회차·상태·정산 경계는
 * 그대로 서버가 본다. 판정할 근거가 없으면(번호를 못 찾음·날짜 없음·만료 방식 모름) 경고를
 * 생략한다 — 개인 수강권은 그때 서버도 막지 않으므로 생략은 잔여 위험이다.
 *
 * 비교 단위
 *  - 개인 수강권: **한국 날짜**. 시작일 당일 수업은 시각과 무관하게 통과한다.
 *  - 공유 수강권: **밀리초**. 서버가 밀리초로 거절하므로(sharedMembershipCapacity.service.ts)
 *    같은 날이라도 시작 시각 전이면 경고한다.
 *  - 만료: `expiryPolicy` 가 `PERIOD`·`FIRST` 일 때만. `COUNT`·없음은 만료일로 끝나지 않는다.
 */

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

/** 기간으로 끝나는 만료 방식. 나머지(COUNT·없음·모름)는 만료일 경고를 하지 않는다. */
const PERIOD_EXPIRY_POLICIES = new Set(['PERIOD', 'FIRST']);

export type ScheduleDateInput = string | number | Date | { valueOf(): number } | null | undefined;

export type ScheduleDateWarningTime = {
    startTime: ScheduleDateInput;
    endTime?: ScheduleDateInput;
};

/** 보낼 요청의 회원 한 명 몫. */
export type ScheduleDateWarningItem = {
    memberId?: string | null;
    /** 확인 창에 보일 회원 이름. 판정에는 쓰지 않는다. */
    memberName?: string | null;
    /** 요청 body 에 실리는 수강권 번호 그대로. */
    membershipId?: string | null;
    /** 요청에 실리는 공유 풀 번호. 있으면 공유로 판정한다. */
    membershipSharePoolId?: string | null;
    times: ScheduleDateWarningTime[];
};

export type ScheduleDateMembershipLike = {
    id?: string | null;
    _id?: string | null;
    name?: string | null;
    activationDate?: string | Date | null;
    expirationDate?: string | Date | null;
    expiryPolicy?: string | null;
    isSharedMembership?: boolean;
    sharedMembershipRole?: string | null;
    membershipSharePoolId?: string | null;
    [key: string]: unknown;
};

export type ScheduleDateWarningKind = 'NOT_STARTED' | 'ENDED';

export type ScheduleDateWarning = {
    memberId: string;
    memberName: string;
    membershipId: string;
    membershipName: string;
    kind: ScheduleDateWarningKind;
    /** 경고에 걸린 회차 수. */
    count: number;
    /** 이 회원 몫으로 보낼 회차 전체 수. 1보다 크면 반복이다. */
    totalCount: number;
    /** 걸린 회차 중 가장 이른 수업의 한국 날짜(YYYY-MM-DD). */
    firstDate: string;
    /** 시작일 또는 만료일의 한국 날짜(YYYY-MM-DD). */
    boundaryDate: string;
    /** 시작일 또는 만료일의 한국 시각(HH:mm). 공유(밀리초 비교)일 때 같은 날 안내에 쓴다. */
    boundaryTime: string;
    /** 비교 단위. 공유는 밀리초라 같은 날이어도 경고가 날 수 있다. */
    precision: 'DATE' | 'INSTANT';
    /**
     * 공유 수강권인가. 공유는 서버가 기간 밖 예약을 항상 409(SHARED_MEMBERSHIP_NOT_USABLE)로
     * 거절하므로(반복은 한 회차라도 걸리면 전체) "그래도 잡기"가 아니라 차단 안내로 다룬다.
     */
    isShared: boolean;
};

const toMs = (value: ScheduleDateInput): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const ms = new Date(value as any).getTime();
    return Number.isFinite(ms) ? ms : null;
};

/** 기기 시간대와 무관하게 한국 날짜를 낸다. 한국은 일광절약시간이 없다. */
export const toKstDateKey = (ms: number): string =>
    new Date(ms + KST_OFFSET_MS).toISOString().slice(0, 10);

const toKstTimeKey = (ms: number): string => new Date(ms + KST_OFFSET_MS).toISOString().slice(11, 16);

const normalizeId = (value: unknown) => String(value ?? '').trim();

const hasValidDate = (value: unknown) => toMs(value as ScheduleDateInput) !== null;

/** 판정에 쓸 수 있는 정보가 얼마나 있나. 만료 방식은 원본(소유자) 카드에만 있어 가장 무겁다. */
const informationScore = (membership: ScheduleDateMembershipLike) =>
    (String(membership.expiryPolicy ?? '').trim() ? 4 : 0) +
    (hasValidDate(membership.activationDate) ? 2 : 0) +
    (hasValidDate(membership.expirationDate) ? 1 : 0);

/** 공유 참여자 카드(권리 응답만으로 만든 합성 카드)인가. 원본 소유자 카드가 아니다. */
const isParticipantCard = (membership: ScheduleDateMembershipLike) =>
    membership.sharedMembershipRole === 'PARTICIPANT';

/** 경고가 나는 쪽(보수적) 날짜를 고른다. 시작일은 늦은 것, 만료일은 이른 것. */
const pickConservativeDate = (
    values: unknown[],
    prefer: 'LATEST' | 'EARLIEST',
): string | Date | null => {
    let picked: { value: unknown; ms: number } | null = null;
    values.forEach((value) => {
        const ms = toMs(value as ScheduleDateInput);
        if (ms === null) return;
        if (!picked || (prefer === 'LATEST' ? ms > picked.ms : ms < picked.ms)) {
            picked = { value, ms };
        }
    });
    return picked ? ((picked as { value: unknown }).value as string | Date) : null;
};

/**
 * 요청에 실린 번호로 찾는다. 화면은 `membership.id` 를 보내고, `_id` 는 같은 문서의 다른 표기다.
 *
 * ⚠️ 같은 번호가 목록에 **여러 장** 있을 수 있다. 1:2 에서 한 공유 수강권을 소유자 카드(원본과
 * 합쳐져 만료 방식까지 있음)와 참여자 카드(권리 응답만으로 만든 합성 카드)로 함께 쓰는 경우다.
 * 첫 일치만 쓰면 목록 순서에 따라 경고가 갈렸다. 그래서 목록 순서와 무관하게
 *  1. 정보(만료 방식·시작일·만료일)가 가장 많은 카드들을 남기고,
 *  2. 그중 원본 소유자 카드(참여자 카드가 아닌 것)가 있으면 그것들만 남기고,
 *  3. 그래도 여러 장이면 날짜를 **경고가 나는 쪽**으로 합친다(늦은 시작일·이른 만료일).
 *     이름·만료 방식은 남은 것 중 목록에서 앞선 카드 것을 쓴다(셋 다 같은 정보 수준이다).
 *  공유 표식은 후보 중 **하나라도** 있으면 공유로 본다.
 */
const resolveMembership = (
    memberships: ScheduleDateMembershipLike[],
    membershipId: string,
): { membership: ScheduleDateMembershipLike; isSharedCandidate: boolean } | undefined => {
    const candidates = memberships.filter(
        (membership) =>
            normalizeId(membership?.id) === membershipId ||
            normalizeId(membership?._id) === membershipId,
    );
    if (candidates.length === 0) return undefined;
    const isSharedCandidate = candidates.some((candidate) => candidate.isSharedMembership === true);

    const topScore = Math.max(...candidates.map(informationScore));
    const richest = candidates.filter((candidate) => informationScore(candidate) === topScore);
    const originals = richest.filter((candidate) => !isParticipantCard(candidate));
    const tied = originals.length > 0 ? originals : richest;
    if (tied.length === 1) return { membership: tied[0], isSharedCandidate };

    return {
        membership: {
            ...tied[0],
            activationDate: pickConservativeDate(
                tied.map((candidate) => candidate.activationDate),
                'LATEST',
            ),
            expirationDate: pickConservativeDate(
                tied.map((candidate) => candidate.expirationDate),
                'EARLIEST',
            ),
        },
        isSharedCandidate,
    };
};

export const findScheduleDateWarnings = (
    items: ScheduleDateWarningItem[],
    memberships: Array<ScheduleDateMembershipLike | null | undefined> | null | undefined,
): ScheduleDateWarning[] => {
    const candidates = (memberships || []).filter(
        (membership): membership is ScheduleDateMembershipLike => !!membership,
    );
    const warnings: ScheduleDateWarning[] = [];

    items.forEach((item) => {
        const membershipId = normalizeId(item?.membershipId);
        // 번호가 없으면 서버가 요청을 거절한다. 번호를 못 찾으면 판정 근거가 없다. 둘 다 생략.
        if (!membershipId) return;
        const resolved = resolveMembership(candidates, membershipId);
        if (!resolved) return;
        const { membership } = resolved;

        const isShared = !!normalizeId(item.membershipSharePoolId) || resolved.isSharedCandidate;
        const precision: ScheduleDateWarning['precision'] = isShared ? 'INSTANT' : 'DATE';

        const startMs = (item.times || [])
            .map((time) => toMs(time?.startTime))
            .filter((ms): ms is number => ms !== null)
            .sort((a, b) => a - b);
        if (startMs.length === 0) return;

        const isBefore = (lessonMs: number, boundaryMs: number) =>
            isShared ? lessonMs < boundaryMs : toKstDateKey(lessonMs) < toKstDateKey(boundaryMs);
        const isAfter = (lessonMs: number, boundaryMs: number) =>
            isShared ? lessonMs > boundaryMs : toKstDateKey(lessonMs) > toKstDateKey(boundaryMs);

        const base = {
            memberId: normalizeId(item.memberId),
            memberName: String(item.memberName ?? '').trim(),
            membershipId,
            membershipName: String(membership.name ?? '').trim(),
            totalCount: startMs.length,
            precision,
            isShared,
        };

        const activationMs = toMs(membership.activationDate as ScheduleDateInput);
        if (activationMs !== null) {
            const hits = startMs.filter((ms) => isBefore(ms, activationMs));
            if (hits.length > 0) {
                warnings.push({
                    ...base,
                    kind: 'NOT_STARTED',
                    count: hits.length,
                    firstDate: toKstDateKey(hits[0]),
                    boundaryDate: toKstDateKey(activationMs),
                    boundaryTime: toKstTimeKey(activationMs),
                });
            }
        }

        // 만료 방식을 모르면(공유 합성 카드 등) 만료 경고를 하지 않는다.
        const expiryPolicy = String(membership.expiryPolicy ?? '').trim().toUpperCase();
        const expirationMs = toMs(membership.expirationDate as ScheduleDateInput);
        if (PERIOD_EXPIRY_POLICIES.has(expiryPolicy) && expirationMs !== null) {
            const hits = startMs.filter((ms) => isAfter(ms, expirationMs));
            if (hits.length > 0) {
                warnings.push({
                    ...base,
                    kind: 'ENDED',
                    count: hits.length,
                    firstDate: toKstDateKey(hits[0]),
                    boundaryDate: toKstDateKey(expirationMs),
                    boundaryTime: toKstTimeKey(expirationMs),
                });
            }
        }
    });

    return warnings;
};

const toMonthDay = (dateKey: string) => {
    const [, month, day] = dateKey.split('-');
    return `${Number(month)}월 ${Number(day)}일`;
};

/** 공유(밀리초)는 수업과 같은 날이어도 걸릴 수 있다. 그때는 시각까지 보여야 이해된다. */
const toBoundaryLabel = (warning: ScheduleDateWarning) => {
    const label = toMonthDay(warning.boundaryDate);
    return warning.precision === 'INSTANT' && warning.firstDate === warning.boundaryDate
        ? `${label} ${warning.boundaryTime}`
        : label;
};

const describeWarning = (warning: ScheduleDateWarning, showMemberName: boolean) => {
    const subject = warning.membershipName ? `이 수강권(${warning.membershipName})은` : '이 수강권은';
    const boundary = toBoundaryLabel(warning);
    const sentence =
        warning.kind === 'NOT_STARTED'
            ? `${subject} ${boundary}부터 사용할 수 있어요.`
            : `${subject} ${boundary}에 끝나요.`;
    const repeat =
        warning.totalCount > 1
            ? warning.kind === 'NOT_STARTED'
                ? ` ${warning.count}회가 시작일 전이에요(첫 날짜 ${toMonthDay(warning.firstDate)}).`
                : ` ${warning.count}회가 만료일 뒤예요(첫 날짜 ${toMonthDay(warning.firstDate)}).`
            : '';
    const prefix = showMemberName && warning.memberName ? `${warning.memberName} 회원: ` : '';
    return `${prefix}${sentence}${repeat}`;
};

/**
 * 확인 창 본문. 경고가 없으면 빈 문자열.
 *
 * 1:2 는 `showMemberName` 을 켠다. 두 회원 중 한 명만 걸려도 **누구의** 수강권인지 보여야
 * 하므로, 경고 개수가 아니라 요청에 실린 회원 수로 호출부가 정한다.
 */
export const formatScheduleDateWarningMessage = (
    warnings: ScheduleDateWarning[],
    { showMemberName = false }: { showMemberName?: boolean } = {},
): string => {
    if (warnings.length === 0) return '';
    const lines = warnings.map((warning) => describeWarning(warning, showMemberName));
    return `${lines.join('\n')}\n\n그래도 이 수강권으로 잡을까요?\n다른 수강권은 취소 후 수강권을 바꿔 주세요.`;
};

/**
 * 공유 수강권 기간 밖 차단 안내 본문. 전송하지 않으므로 질문 없이 다음 행동만 알린다.
 * 공유는 밀리초로 거절되므로 시각까지 보여 준다.
 */
export const formatSharedDateBlockMessage = (
    warnings: ScheduleDateWarning[],
    { showMemberName = false }: { showMemberName?: boolean } = {},
): string => {
    const shared = warnings.filter((warning) => warning.isShared);
    if (shared.length === 0) return '';
    const lines = shared.map((warning) => {
        const subject = warning.membershipName
            ? `이 공유 수강권(${warning.membershipName})은`
            : '이 공유 수강권은';
        const boundary = `${toMonthDay(warning.boundaryDate)} ${warning.boundaryTime}`;
        const sentence =
            warning.kind === 'NOT_STARTED'
                ? `${subject} ${boundary}부터 사용할 수 있어요.`
                : `${subject} ${boundary}에 끝나요.`;
        const repeat =
            warning.totalCount > 1
                ? warning.kind === 'NOT_STARTED'
                    ? ` ${warning.count}회가 시작 전이에요(첫 날짜 ${toMonthDay(warning.firstDate)}).`
                    : ` ${warning.count}회가 끝난 뒤예요(첫 날짜 ${toMonthDay(warning.firstDate)}).`
                : '';
        const prefix = showMemberName && warning.memberName ? `${warning.memberName} 회원: ` : '';
        return `${prefix}${sentence}${repeat}`;
    });
    return `${lines.join('\n')}\n\n날짜를 바꾸거나 다른 수강권을 골라 주세요.`;
};
